import React, { useEffect, useState, useRef } from "react";
import {
    Radar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    ResponsiveContainer,
} from "recharts";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { getBarChartData, getLabelData } from "../../slices/barChart/barChartThunks";
import { styled } from "styled-components";
import { IconButton } from "../../components/ButtonsNew";
import { Download, LeafIcon } from "../../shared/icons";
import { screenSizes } from "../../styles/theme";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 4rem;
    @media (max-width: ${screenSizes.L}px) {
      flex-direction: column;
      margin-top: 0;
      align-items: center;
  }
`;

const LeftContainer = styled.div`
  width: 50%;
  padding: 20px;
  @media (max-width: ${screenSizes.L}px) {
    width: auto;
    margin-inline: 1rem;
    padding-block: 1rem;
    padding-inline: 0;
  }
`;

const Title = styled.h3`
  font-size: 50px;
  font-weight: 700;
  line-height: 1.4;
  margin-bottom: 2rem;
  letter-spacing: -2px;
  @media (max-width: ${screenSizes.L}px) {
    font-size: 32px;
    margin-bottom: 1rem;
    line-height: 40px;
    letter-spacing: 0;
  }
`;

const List = styled.ul`
  list-style: disc;
  padding-left: 20px;
  line-height: 1.8;
  margin-left: 10px;
  @media (max-width: ${screenSizes.L}px) {
    margin-left: 20px !important;
    width: 90%;
    padding-left: 0;
  }
`;

const ListItem = styled.li`
  font-size: 16px;
  color: #091c00;
  margin-bottom: 10px;
  p {
    color: #091c00cc;
    line-height: 1.5;
    font-weight: 400;
    margin: 5px 0;
  }
`;

const ChartContainer = styled.div`
  width: 45%;
  position: relative;
  @media (max-width: ${screenSizes.L}px) {
    width: 90%;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  right: -2rem;
  @media (max-width: ${screenSizes.L}px) {
    right: 30%;
  }
  button {
    width: 190px;
  }
`;

const MobileIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-block: 10px;
  button {
    width: auto;
    padding: 8px 15px;
  }
`

const ScoreContainer = styled.div`
  background-color: #F1E5C0;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  margin: auto;
  height: 209px;
  justify-content: center;
  gap: 15px;
  @media (max-width: ${screenSizes.L}px) {
    height: auto;
    width: auto;
    margin-inline: 1rem;
    margin-top: -3rem;
    margin-bottom: 2rem;
    padding: 12px;
    gap: 0;
  }
`;

const ScoresRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  > *:not(:last-child)::after {
    content: " | ";
    margin-left: 10px;
    font-size: 34px;
  }
  @media (max-width: ${screenSizes.L}px) {
    flex-direction: column;
    align-items: start;
    width: 100%;
    > *:not(:last-child)::after {
    content: "";
    margin-left: 0;
  }
  }
`;

const ScoreItem = styled.span<{type?: any}>`
  font-size: 14px;
  font-weight: 400;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  display: flex;
  @media (max-width: ${screenSizes.L}px) {
    padding: 1rem 0;
    width: 100%;
    justify-content: flex-start;
    border-bottom: 1px solid #091C0080;
  }
  span {
    background-color: #FF9D59;
    border-radius: 15px;
    padding: 2px;
    min-width: 51px;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }
`;

const OverallImpact = styled.div<{value: any}>`
  padding-top: 15px;
  font-weight: 600;
  border-top: 1px solid #091C00;
  text-align: center;
  width: 100%;
  font-size: 18px;
  @media (max-width: ${screenSizes.L}px) {
    padding-top: 0;
    padding: 1rem;
    border-top: 0.5px solid #091C00;
  }
  span {
    background-color: ${({ value }) =>
    value <= 3 ? "#C7FFC5" : value >= 6 ? "#FFC5C5" : "#feffc5"};
    border: 1px solid #091C001A;
    color: #091C00;
    padding: 2px 10px;
    border-radius: 40px;
    font-size: 14px;
    margin-left: 6px;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    min-width: 54px;
    font-weight: 400;
  }
`;
interface Props {
    id: any;
    name: any;
    score: any
    networkId?: number;
}

const CustomRadarChart: React.FC<Props> = ({ id, name, score, networkId }) => {
    const chartRef = useRef(null);
    const chartLabelsRef = useRef(null);
    const chartScoreRef = useRef(null);
    const dispatch: AppDispatch = useDispatch();
    const [chartData, setChartData] = useState([]);
    const [labelData, setLabelData] = useState([]);
    const isMobile = window.innerWidth <= 1024;

    const getBarChartRecord = () => {
        dispatch(getBarChartData(id as string))
            .then((res: any) => {
                setChartData(res?.payload?.data?.data?.radarData);
            })
            .catch((err) => {
                console.error("Error fetching bar chart data:", err);
            });
    };

    const getLabelDetails = () => {
        dispatch(getLabelData())
            .then((res: any) => {
                setLabelData(res?.payload?.data?.radarLabels);
            })
            .catch((err) => {
                console.error("Error fetching label data:", err);
            });
    };

    useEffect(() => {
        getBarChartRecord();
        getLabelDetails();
        console.log({name, networkId, score})
    }, [id]);

    const renderCustomTick = (props: any) => {
      const { payload, x, y, textAnchor, index } = props;
      const words = payload.value.split(" ");
      const isLongText = words.length > 3;
      const radiusOffset = 10;
      const angle = (index * (360 / 6)) * (Math.PI / 180);
      const adjustedX = x + Math.cos(angle) * radiusOffset;
      const adjustedY = y + Math.sin(angle) * radiusOffset;
    
      return (
        <text x={adjustedX} y={adjustedY} textAnchor={textAnchor} fontSize="12" fill="#000">
          {isLongText
            ? words.reduce((lines: string[], word: string | number, index: number) => {
                const lineIndex = Math.floor(index / 3);
                if (!lines[lineIndex]) lines[lineIndex] = "";
                lines[lineIndex] += (lines[lineIndex] ? " " : "") + word;
                return lines;
              }, []).map((line: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined, index: React.Key | null | undefined) => (
                <tspan x={adjustedX} dy={index === 0 ? "0" : "1.2em"} key={index}>
                  {line}
                </tspan>
              ))
            : payload.value}
        </text>
      );
    };

    const handleExportPDF = async () => {
      const networdID = (typeof networkId === "number") ? networkId === 1 ? "ETH" : networkId === 137 ? "POL" : "HBAR" : networkId || "";
      const scoreValue = score.toFixed(1);
      const pdf = new jsPDF("portrait", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      let yPosition = 20; 
      const addCenteredImage = async (pdf:any, ref:any, y:any, maxWidth:any, maxHeight:any) => {
        if (!ref.current) return;

        const canvas = await html2canvas(ref.current, { scale: 2 });
        const imgData = canvas.toDataURL("image/png");

        const aspectRatio = canvas.width / canvas.height;
        let imgWidth = maxWidth;
        let imgHeight = imgWidth / aspectRatio;

        if (imgHeight > maxHeight) {
          imgHeight = maxHeight;
          imgWidth = maxHeight * aspectRatio;
        }

        const xPos = (pdfWidth - imgWidth) / 2;
        pdf.addImage(imgData, "PNG", xPos, y, imgWidth, imgHeight);

        return imgHeight;
      };

      // Name
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.text(name, 10,yPosition);
      yPosition += 10;

      // Chain
      pdf.setFont("helvetica", "bold");
      pdf.text("Chain:", 10, yPosition);
      pdf.setFont("helvetica", "normal");
      pdf.text(networdID, 30, yPosition);
      yPosition += 10;

      // Impact Score
      pdf.setFont("helvetica", "bold");
      pdf.text("Impact Score:", 10, yPosition);
      pdf.setFont("helvetica", "normal");
      pdf.text(scoreValue, 50, yPosition);
      yPosition += isMobile ?  1  : 20;

      // Chart Image
      if (chartRef.current) {
        const chartHeight: any  = await addCenteredImage(pdf, chartRef, yPosition, pdfWidth, 135)
        yPosition += isMobile ? chartHeight-10 :  chartHeight + 20;
      }
      // Score Image
      if (chartScoreRef.current) {
        await addCenteredImage(pdf, chartScoreRef, yPosition, pdfWidth - 10,120);
    }
    pdf.addPage();
    yPosition = 10;
    if (chartLabelsRef.current) {
      await addCenteredImage(pdf, chartLabelsRef, yPosition, pdfWidth, 250);
    }
    // Save PDF
    pdf.save("vyga-report.pdf");
  };
    
    return (
      <>
        <Container>
            <LeftContainer ref={chartLabelsRef}>
                <Title>
                    AI-Powered Ranking
                    <br />
                    Across Key Metrics
                </Title>
                <List>
                    {labelData.map((data: any, index) => (
                        <ListItem key={index}>
                            <strong>{data.key}:</strong>
                            <p>{data.description}</p>
                        </ListItem>
                    ))}
                </List>
            </LeftContainer>
            <ChartContainer>
                {!isMobile && <IconWrapper>
                    <IconButton icon={<img src={Download} />} onClick={handleExportPDF}>Export Report</IconButton>
                </IconWrapper>}
              <div
                id="chart-container"
                ref={chartRef}
              >
                <ResponsiveContainer
                    width="100%"
                    height={400}
                    style={{marginTop: isMobile? "-5rem":"6rem"}}
                >
                    <RadarChart cx="50%" cy="50%" outerRadius={isMobile ? "50%" : "90%"} data={chartData}>
                        <PolarGrid stroke="#D3D3D3" radialLines={false} />
                        <PolarAngleAxis
                            dataKey="subject"
                            axisLine={false}
                            tick={renderCustomTick}
                        />
                        <PolarRadiusAxis
                            angle={50}
                            domain={[0, 100]}
                            tick={{ fill: "#D3D3D3", fontSize: 12, fontWeight: 700 }}
                            tickCount={6}
                            axisLine={false}
                            stroke="#ddd"
                        />
                        <Radar
                            name="Metrics"
                            dataKey="A"
                            stroke="#1a1a1a"
                            fill="#d9d9d9"
                            fillOpacity={0.5}
                            dot={{ fill: "#ff7f50", r: 5, stroke: "none", fillOpacity: 1 }}
                            strokeWidth={3}
                        />
                    </RadarChart>
                </ResponsiveContainer>
                </div>
            </ChartContainer>
        </Container>
        <ScoreContainer ref={chartScoreRef}>
          <ScoresRow>
            {chartData?.map((item: { _id: React.Key | null | undefined; subject: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; A: number; }, index: number) => (
              <ScoreItem key={item._id} type={index === 0 ? "high" : undefined}>
                {item.subject} &nbsp;<span>{item.A}</span>
              </ScoreItem>
            ))}
          </ScoresRow>
          <OverallImpact value={score}>
                Overall Impact Score <span><LeafIcon />&nbsp;{score?.toFixed(1) || 0}</span>
          </OverallImpact>
        </ScoreContainer>
        {isMobile && <MobileIconWrapper>
            <IconButton icon={<img src={Download} />} onClick={handleExportPDF}>Export Report</IconButton>
        </MobileIconWrapper>}
      </>
    );
};

export default CustomRadarChart;
